import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useBuilding } from "../../context/BuildingContext";
import { bankAccountsApi } from "../../services/api";
import PageHeader from "../../components/common/PageHeader";
import ErrorAlert from "../../components/common/ErrorAlert";
import { BankAccountType } from "@akfdev-com/shared/src/models";

interface BankAccountForm {
  name: string;
  bankName: string;
  accountType: BankAccountType;
  accountNumber: string;
  sortCode: string;
  currency: string;
  openingBalance: string;
  description: string;
}

const accountTypeOptions = Object.values(BankAccountType).map((v) => ({
  value: v,
  label: v.replace(/([A-Z])/g, " $1").trim(),
}));

const defaultForm: BankAccountForm = {
  name: "",
  bankName: "",
  accountType: BankAccountType.CurrentAccount,
  accountNumber: "",
  sortCode: "",
  currency: "GBP",
  openingBalance: "0",
  description: "",
};

const BankAccountNew: React.FC = () => {
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const buildingId = currentBuilding?.id ?? "";

  const [form, setForm] = useState<BankAccountForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!buildingId) {
      setError("No building selected. Please select a building first.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await bankAccountsApi.create({
        ...form,
        openingBalance: Math.round(
          parseFloat(form.openingBalance || "0") * 100,
        ),
        buildingId,
        isActive: true,
      });

      toast.success("Bank account created successfully.");
      navigate("/dashboard/banking");
    } catch (err) {
      console.error("Error creating bank account:", err);
      setError("Failed to create bank account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Add Bank Account"
        description="Create a new bank account for this building."
      />

      {error && <ErrorAlert message={error} />}

      <form
        onSubmit={handleSubmit}
        className="mt-6 bg-white shadow rounded-lg p-4 sm:p-6 space-y-5 w-full max-w-2xl"
      >
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="name"
            >
              Account Name <span className="text-red-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              type="text"
              required
              value={form.name}
              onChange={handleChange}
              placeholder="e.g. Business Current Account"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="bankName"
            >
              Bank Name
            </label>
            <input
              id="bankName"
              name="bankName"
              type="text"
              value={form.bankName}
              onChange={handleChange}
              placeholder="e.g. Barclays"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="accountType"
            >
              Account Type
            </label>
            <select
              id="accountType"
              name="accountType"
              value={form.accountType}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            >
              {accountTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="accountNumber"
            >
              Account Number
            </label>
            <input
              id="accountNumber"
              name="accountNumber"
              type="text"
              value={form.accountNumber}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="sortCode"
            >
              Sort Code
            </label>
            <input
              id="sortCode"
              name="sortCode"
              type="text"
              value={form.sortCode}
              onChange={handleChange}
              placeholder="e.g. 20-00-00"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="currency"
            >
              Currency
            </label>
            <input
              id="currency"
              name="currency"
              type="text"
              value={form.currency}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="openingBalance"
            >
              Opening Balance (£)
            </label>
            <input
              id="openingBalance"
              name="openingBalance"
              type="number"
              step="0.01"
              value={form.openingBalance}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label
              className="block text-sm font-medium text-gray-700"
              htmlFor="description"
            >
              Description
            </label>
            <textarea
              id="description"
              name="description"
              rows={3}
              value={form.description}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 text-base sm:text-sm"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate("/dashboard/banking")}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 min-h-[44px] sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-base sm:text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 min-h-[44px] sm:min-h-0"
          >
            {submitting ? "Creating…" : "Create Account"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BankAccountNew;
